import React, { useState, useEffect, useCallback } from 'react';
import { API, getToken } from './adminApi';
import DirectComposerPanel from './directEmail/DirectComposerPanel';
import EmailHistoryPanel from './EmailHistoryPanel';

// Trimmed sibling of GranularInterconnectingEmails.js — same
// community-select / live-preview / send / round-history shape, but no
// suggest/pairing UI at all: a direct email introduces nobody, it just
// goes to whoever's ticked "Email".
const DirectEmailTab = () => {
  const [communities, setCommunities] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [communityTitle, setCommunityTitle] = useState('');
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const [emailedUserIds, setEmailedUserIds] = useState(new Set());

  const [composerMode, setComposerMode] = useState('shared');
  const [sharedTemplate, setSharedTemplate] = useState({ subject: '', body: '' });
  const [perRecipientDrafts, setPerRecipientDrafts] = useState({});
  const [roundLabel, setRoundLabel] = useState(''); // optional name for this batch, shown in Send History
  // Personal "from James" touches, applied server-side at render time (see
  // backend/services/emailSignature.js) — deliberately not reset on
  // community change, since they're a sender preference, not round state.
  const [includeSignature, setIncludeSignature] = useState(true);
  const [sendAsMe, setSendAsMe] = useState(true);

  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewUpdating, setPreviewUpdating] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [actionError, setActionError] = useState('');

  const [showRoundHistory, setShowRoundHistory] = useState(false);
  const [roundHistory, setRoundHistory] = useState(null);
  const [roundHistoryLoading, setRoundHistoryLoading] = useState(false);
  const [expandedRoundId, setExpandedRoundId] = useState(null);
  const [viewingHistoryUserId, setViewingHistoryUserId] = useState(null);

  const authedFetch = useCallback(async (path, options = {}) => {
    const token = await getToken();
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Request failed');
    return json;
  }, []);

  useEffect(() => {
    fetch(`${API}/communities`)
      .then((res) => res.json())
      .then((data) => setCommunities(Array.isArray(data)
        ? [...data].sort((a, b) => (a.title || '').localeCompare(b.title || ''))
        : []))
      .catch(() => setCommunities([]));
  }, []);

  const resetRoundState = () => {
    setEmailedUserIds(new Set());
    setComposerMode('shared');
    setSharedTemplate({ subject: '', body: '' });
    setPerRecipientDrafts({});
    setRoundLabel('');
    setPreviewPlan(null);
    setSendResult(null);
    setActionError('');
    setRoundHistory(null);
    setShowRoundHistory(false);
    setExpandedRoundId(null);
  };

  const fetchMembers = useCallback(async (communityId) => {
    if (!communityId) return;
    setLoading(true);
    setFetchError('');
    try {
      const data = await authedFetch(`/admin/communities/${communityId}/members`);
      setCommunityTitle(data.communityTitle);
      setMembers(data.members || []);
    } catch (err) {
      setFetchError(err.message);
      setMembers(null);
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const fetchRoundHistory = useCallback(async (communityId) => {
    setRoundHistoryLoading(true);
    try {
      const data = await authedFetch(`/admin/direct-email/rounds?communityId=${communityId}`);
      setRoundHistory(data.rounds || []);
    } catch (err) {
      setRoundHistory([]);
    } finally {
      setRoundHistoryLoading(false);
    }
  }, [authedFetch]);

  const toggleRoundHistory = () => {
    const next = !showRoundHistory;
    setShowRoundHistory(next);
    if (next && roundHistory === null && selectedId) fetchRoundHistory(selectedId);
  };

  const handleSelectCommunity = (e) => {
    const communityId = e.target.value;
    setSelectedId(communityId);
    setMembers(null);
    setFetchError('');
    resetRoundState();
    if (communityId) {
      fetchMembers(communityId);
    }
  };

  const toggleEmailed = (userId) => {
    setEmailedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const buildRequestBody = () => ({
    communityId: selectedId,
    emailedUserIds: [...emailedUserIds],
    composerMode,
    sharedTemplate,
    perRecipientDrafts,
    includeSignature,
    sendAsMe,
    label: roundLabel,
  });

  const handlePreview = useCallback(async (body) => {
    setPreviewUpdating(true);
    setActionError('');
    try {
      const plan = await authedFetch('/admin/direct-email/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setPreviewPlan(plan);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setPreviewUpdating(false);
    }
  }, [authedFetch]);

  // Live-updating preview, same pattern as the Interconnecting composer —
  // calls the same backend render path /direct-email/send uses, so what's
  // shown here is exactly what a send will do.
  const requestBodySignature = JSON.stringify(buildRequestBody());
  useEffect(() => {
    if (!selectedId || emailedUserIds.size === 0) {
      setPreviewPlan(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      handlePreview(JSON.parse(requestBodySignature));
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestBodySignature, selectedId, emailedUserIds.size]);

  const handleSend = async () => {
    if (!previewPlan) return;
    const confirmed = window.confirm(
      `Send ${previewPlan.recipients.length} email(s) now? This cannot be undone.`
    );
    if (!confirmed) return;

    setSendLoading(true);
    setActionError('');
    try {
      const result = await authedFetch('/admin/direct-email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildRequestBody()),
      });
      setSendResult(result);
      if (showRoundHistory) fetchRoundHistory(selectedId);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <div className="as-signups">
      {fetchError && <div className="as-error">{fetchError}</div>}
      {actionError && <div className="as-error">{actionError}</div>}

      <div className="as-activity-toolbar">
        <select className="as-filter-tab" value={selectedId} onChange={handleSelectCommunity}>
          <option value="">Select a community…</option>
          {communities.map((c) => (
            <option key={c._id} value={c._id}>{c.title}</option>
          ))}
        </select>
        {selectedId && (
          <button className="as-filter-tab" onClick={() => fetchMembers(selectedId)}>↻ Refresh</button>
        )}
      </div>

      {loading && <div className="as-loading">Loading…</div>}

      {members && (
        <>
          <h2 className="as-section-title">{communityTitle} — Create Message ({members.length})</h2>
          {members.length === 0 && <div className="as-empty">No members in this community.</div>}

          <div className="as-signup-list as-signup-list--grid">
            {members.map((m) => {
              const alreadyEmailed = emailedUserIds.has(m.userId);
              return (
                <div
                  key={m.userId}
                  className={`as-signup-card as-signup-card--clickable${alreadyEmailed ? ' as-signup-card--selected' : ''}`}
                  onClick={() => toggleEmailed(m.userId)}
                >
                  <div className="as-signup-select">
                    <input
                      type="checkbox"
                      checked={alreadyEmailed}
                      onChange={() => toggleEmailed(m.userId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {m.photo ? <img src={m.photo} alt="" className="as-signup-img" /> : <div className="as-signup-img-placeholder">?</div>}
                  <div className="as-signup-details">
                    <div className="as-signup-headline">
                      <strong>{m.firstName || '(no name)'}</strong>
                      {m.age != null && <span className="as-signup-age">{m.age}</span>}
                      {m.gender && <span className="as-card-meta"> · {m.gender}</span>}
                    </div>
                    <div className="as-card-meta">{m.localArea || 'Location not set'}</div>
                    <button type="button" className="as-text-link" onClick={(e) => { e.stopPropagation(); setViewingHistoryUserId(m.userId); }}>View email history</button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="as-email-field">
            <label>Batch name (optional — shown in Send History, not sent to recipients)</label>
            <input type="text" value={roundLabel} onChange={(e) => setRoundLabel(e.target.value)} placeholder="e.g. Welcome message for new marketing volunteers" />
          </div>

          <DirectComposerPanel
            composerMode={composerMode}
            setComposerMode={setComposerMode}
            sharedTemplate={sharedTemplate}
            setSharedTemplate={setSharedTemplate}
            perRecipientDrafts={perRecipientDrafts}
            setPerRecipientDrafts={setPerRecipientDrafts}
            emailedMembers={(members || []).filter((m) => emailedUserIds.has(m.userId))}
            communityId={selectedId}
            authedFetch={authedFetch}
          />

          <div className="as-activity-toolbar">
            <label className="as-suggestion-checkbox-label">
              <input type="checkbox" checked={sendAsMe} onChange={(e) => setSendAsMe(e.target.checked)} />
              Send as me (From: "James at {communityTitle}")
            </label>
            <label className="as-suggestion-checkbox-label">
              <input type="checkbox" checked={includeSignature} onChange={(e) => setIncludeSignature(e.target.checked)} />
              Add my signature
            </label>
          </div>

          {emailedUserIds.size > 0 && (
            <div className="as-email-preview">
              <div className="as-card-meta">
                {previewUpdating ? 'Updating preview…' : 'Preview'}
                {previewPlan && (
                  <button className="as-btn as-btn--approve" style={{ marginLeft: '0.75rem' }} onClick={handleSend} disabled={sendLoading || previewUpdating || previewPlan.recipients.length === 0}>
                    {sendLoading ? 'Sending…' : `📤 Send (${previewPlan.recipients.length})`}
                  </button>
                )}
              </div>
              {previewPlan?.warnings.length > 0 && (
                <ul className="as-suggestion-warnings">
                  {previewPlan.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                </ul>
              )}
              {previewPlan && (
                <>
                  <div className="as-email-preview-from">From: {previewPlan.community.senderName ? `${previewPlan.community.senderName} <${previewPlan.community.senderAddress}>` : previewPlan.community.senderAddress}</div>
                  {previewPlan.recipients.map((r) => (
                    <div key={r.userId} className="as-suggestion-recipient-card">
                      <div className="as-card-meta"><strong>To:</strong> {r.firstName} ({r.email})</div>
                      <div className="as-card-meta"><strong>Subject:</strong> {r.subject}</div>
                      <div className="as-suggestion-body-preview" dangerouslySetInnerHTML={{ __html: r.htmlBody }} />
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {sendResult && (
            <div className="as-action-msg">
              {sendResult.dryRun ? '🧪 DRY RUN — ' : '✅ '}
              Attempted {sendResult.attempted}: {sendResult.sent} sent, {sendResult.dry_run} dry-run, {sendResult.failed} failed.
              {sendResult.failed > 0 && sendResult.firstError && (
                <div className="as-error" style={{ marginTop: '0.5rem' }}>Why it failed: {sendResult.firstError}</div>
              )}
            </div>
          )}

          <div className="as-activity-toolbar" style={{ marginTop: '1.5rem' }}>
            <button className="as-filter-tab" onClick={toggleRoundHistory}>
              {showRoundHistory ? '▾' : '▸'} Send History
            </button>
          </div>

          {showRoundHistory && (
            <div>
              {roundHistoryLoading && <div className="as-loading">Loading…</div>}
              {roundHistory && roundHistory.length === 0 && <div className="as-empty">No direct emails sent yet for this community.</div>}
              {roundHistory && roundHistory.length > 0 && (
                <table className="as-ab-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>When</th>
                      <th>Sent by</th>
                      <th>Mode</th>
                      <th>Recipients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roundHistory.map((round) => (
                      <React.Fragment key={round._id}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedRoundId(expandedRoundId === round._id ? null : round._id)}>
                          <td>{round.label || <span className="as-card-meta">(unnamed)</span>}</td>
                          <td>{new Date(round.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td>{round.createdBy}</td>
                          <td>{round.composerMode}</td>
                          <td>{round.recipients.length}</td>
                        </tr>
                        {expandedRoundId === round._id && round.recipients.map((r, i) => (
                          <tr key={i}>
                            <td colSpan={5}>
                              <div className="as-suggestion-recipient-card">
                                <div className="as-card-meta"><strong>To:</strong> {r.email} — {r.status}{r.errorMessage ? ` (${r.errorMessage})` : ''}</div>
                                <div className="as-card-meta"><strong>Subject:</strong> {r.subject}</div>
                                <div className="as-suggestion-body-preview" dangerouslySetInnerHTML={{ __html: r.htmlBody }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {viewingHistoryUserId && (
        <EmailHistoryPanel userId={viewingHistoryUserId} onClose={() => setViewingHistoryUserId(null)} />
      )}
    </div>
  );
};

export default DirectEmailTab;
